<template>
  <div
    :class="[
      'mood-inverse fixed inset-0 z-50 flex flex-col bg-[#191c20] text-static-default-hi',
      $style.grid,
    ]"
  >
    <div class="flex justify-end p-12">
      <RevButtonIcon
        :alternative-text="i18n(translations.close)"
        :icon="IconCross"
        variant="ghost"
        @click="onClose"
      />
    </div>

    <div class="flex flex-1 items-center justify-center px-16 pb-24">
      <div
        ref="imageArea"
        class="relative aspect-square w-full max-w-[640px] touch-none"
      >
        <RevIllustration
          v-if="product"
          :alt="product.title"
          class="pointer-events-none h-full w-full object-contain"
          :height="1200"
          :src="inspectImage"
          :style="{ 'view-transition-name': 'outlet-inspect' }"
          :width="1200"
        />

        <!-- Magnifier lens: a circle showing the image under the pointer,
             zoomed, with a soft inner glass highlight. Follows the
             pointer/touch; rests near the top-left otherwise. -->
        <div aria-hidden="true" :class="$style.lens" :style="lensStyle" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from '#imports'
import {
  type CSSProperties,
  computed,
  nextTick,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevIllustration } from '@ds/components/Illustration'
import { IconCross } from '@ds/icons/IconCross'
import { useMouseInElement, usePreferredReducedMotion } from '@vueuse/core'

import { useOutletProduct } from '../../composables/useOutletProduct'
import { getOutletProductImage } from '../../utils/getOutletProductImage'

import translations from './OutletInspect.translations'

const LENS_SIZE = 160
const ZOOM = 2.4
// Lift the lens above the touch point so the magnified detail sits clear of the
// user's fingertip while panning, instead of hiding behind it. The lens still
// magnifies the exact point under the finger (the background mapping is unchanged
// below) — only its on-screen position is shifted upward.
const LENS_VERTICAL_OFFSET = 88

const route = useRoute()
const router = useRouter()
const i18n = useI18n()
const { trackClick } = useTracking()

const objectId = computed(() => String(route.params.id))
const { data: product } = useOutletProduct(objectId)

// Inspect the exact image the user tapped (passed via query), falling back to
// the default product image on a direct visit.
const inspectImage = computed(() => {
  const queried = route.query.img

  return typeof queried === 'string' && queried
    ? queried
    : getOutletProductImage(product.value)
})

const imageArea = useTemplateRef<HTMLElement>('imageArea')
const { elementX, elementY, elementWidth, elementHeight, isOutside } =
  useMouseInElement(imageArea)

// Remember the last point the user actually panned to. Updated only while the
// pointer is inside the image (actively panning), so when panning stops — or
// during the closing transition — the lens holds its last position instead of
// snapping back to the resting spot.
const pannedX = ref<number | null>(null)
const pannedY = ref<number | null>(null)

watch([elementX, elementY, isOutside], ([x, y, outside]) => {
  if (!outside) {
    pannedX.value = x
    pannedY.value = y
  }
})

// Before any interaction the lens rests at 30%/30% so it's always visible (like
// the design); once panned it stays wherever it was last moved to.
const lensX = computed(() => pannedX.value ?? elementWidth.value * 0.3)
const lensY = computed(() => pannedY.value ?? elementHeight.value * 0.3)

const lensStyle = computed<CSSProperties>(() => {
  const width = elementWidth.value
  const height = elementHeight.value
  if (!width || !height) return { display: 'none' }

  const x = Math.min(width, Math.max(0, lensX.value))
  const y = Math.min(height, Math.max(0, lensY.value))

  return {
    width: `${LENS_SIZE}px`,
    height: `${LENS_SIZE}px`,
    left: `${x - LENS_SIZE / 2}px`,
    top: `${y - LENS_SIZE / 2 - LENS_VERTICAL_OFFSET}px`,
    backgroundColor: '#24262b',
    backgroundImage: `url(${inspectImage.value})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${width * ZOOM}px ${height * ZOOM}px`,
    backgroundPosition: `${LENS_SIZE / 2 - x * ZOOM}px ${LENS_SIZE / 2 - y * ZOOM}px`,
  }
})

const reducedMotion = usePreferredReducedMotion()

// Morph back to the PDP carousel (the shared `outlet-inspect` transition name)
// when available; otherwise just navigate back.
function onClose() {
  trackClick({
    zone: 'outlet_inspect',
    name: 'close',
  })

  if (!document.startViewTransition || reducedMotion.value === 'reduce') {
    router.back()

    return
  }

  document.startViewTransition(async () => {
    router.back()
    await nextTick()
  })
}
</script>

<style module>
.grid {
  background-color: #24262b;
  background-image:
    linear-gradient(to left, #34373f 1px, transparent 1px),
    linear-gradient(to top, #34373f 1px, transparent 1px);
  background-size: 5vw 5vw;
}

.lens {
  position: absolute;
  border-radius: 9999px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  /* Outer drop shadow + a soft inner glass highlight. */
  box-shadow:
    inset 0 8px 18px -6px rgba(255, 255, 255, 0.55),
    inset 0 0 24px rgba(255, 255, 255, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.4),
    0 8px 24px rgba(0, 0, 0, 0.45);
  pointer-events: none;
}
</style>
